import { ResizeObserver } from '@juggle/resize-observer';
import clsx from 'clsx';
import {
  type ChangeEvent,
  type CSSProperties,
  type InputHTMLAttributes,
  useId,
  useState,
} from 'react';

import useMeasure from 'react-use-measure';
import './range.css';

interface RangeProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /**
   * Aria label
   * @default 'Range'
   * */
  ariaLabel?: string;
  /**
   * Additional class names
   * */
  className?: string;
  /**
   * Data test id
   * */
  dataTestId?: string;
  /**
   * Single cursor mode
   * @default false
   * */
  singleCursorMode?: boolean;
  /**
   * Min value
   * @default 0
   * */
  min?: number;
  /**
   * On change
   * */
  onChange?: (name: string, value: number[]) => void;
  /**
   * Options
   * @default []
   * */
  options: { value: number; label: string }[];
}

export const Range = (props: RangeProps) => {
  const internalId = useId();

  const {
    ariaLabel,
    className,
    dataTestId = 'range',
    id = internalId,
    name = id,
    singleCursorMode = false,
    min = 0,
    options = [],
    step = 1,
    readOnly,
    disabled,
    onChange,
    ...rest
  } = props;

  const [valueA, setValueA] = useState(options.length - 1);
  const [valueB, setValueB] = useState(min);
  const [containerRef, { width }] = useMeasure({ polyfill: ResizeObserver });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!readOnly) {
      e.persist();
      const inputValue = Number(e.target.value);
      const inputId = e.target.id[e.target.id.length - 1];

      if (inputId === 'a') {
        setValueA(inputValue);
      } else {
        setValueB(inputValue);
      }

      if (onChange) {
        onChange(name, [valueA, valueB]);
      }
    }
  };

  return (
    <div
      style={
        {
          '--min': min,
          '--max': options.length - 1,
          '--inputWidth': `${width}px`,
          '--inputHeight': '4px',
          '--thumbWidth': '32px',
          '--usefulWidth': 'calc(var(--inputWidth) - var(--thumbWidth))',
          '--valueA': valueA,
          '--valueB': valueB,
        } as CSSProperties
      }
      className={clsx('relative w-full', className)}
      data-testid={dataTestId}
      data-name="Range"
      ref={containerRef}
    >
      <div className="input-range-container">
        <input
          {...rest}
          aria-label={ariaLabel}
          className="input-range"
          id={`${id}-a`}
          max={options.length - 1}
          min={min}
          name={name}
          onChange={handleChange}
          disabled={disabled}
          step={step}
          type="range"
          value={valueA}
        />
        {!singleCursorMode && (
          <input
            {...rest}
            aria-label={ariaLabel}
            className="input-range"
            disabled={disabled}
            id={`${id}-b`}
            max={options.length - 1}
            min={min}
            name={name}
            onChange={handleChange}
            step={step}
            type="range"
            value={valueB}
          />
        )}
      </div>
      <output
        htmlFor={`${id}-a`}
        style={{ '--value': valueA } as CSSProperties}
        className="input-range-output"
      >
        {options[valueA].label}
      </output>
      {!singleCursorMode && (
        <output
          htmlFor={`${id}-b`}
          style={{ '--value': valueB } as CSSProperties}
          className="input-range-output"
        >
          {options[valueB].label}
        </output>
      )}
    </div>
  );
};
